<script>
	//NPM Modules
	import { createEventDispatcher } from 'svelte';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	import SelectInput from '@/Components/SelectInput.svelte';
	import SportcastLeague from '@/Models/SportcastLeague.js';
	import ACLStore from '@/Stores/ACLStore.js';

	export let competition;
	export let open;

	const dispatch = createEventDispatcher();

	let sending = false;
	let errors;

	let selectedLeague = competition.sportcast_competition_id ?? null;

	$: availableLeagues = [];
	$: availableSports = availableLeagues && filterLeaguesToSports();

	$: filterSportId = null;
	$: filterString = null;
	$: filteredAvailableLeagues =
		availableLeagues &&
		(filterSportId || !filterSportId) &&
		(filterString || !filterString) &&
		filterAvailableLeagues();

	const sorter = (a, b) => {
		let x = a.name.toLowerCase();
		let y = b.name.toLowerCase();

		if (x > y) {
			return 1;
		}
		if (x < y) {
			return -1;
		}
		return 0;
	};

	function filterLeaguesToSports() {
		const sports = [];
		for (const league of availableLeagues) {
			sports.push({
				id: league.sport_id,
				name: league.sport_name
			});
		}
		const unique = [...new Map(sports.map((item) => [item.id, item])).values()];
		return unique.sort(sorter);
	}

	function filterAvailableLeagues() {
		let availableLeaguesFiltered = availableLeagues ?? [];
		if (filterSportId) {
			availableLeaguesFiltered = availableLeaguesFiltered.filter(function (league) {
				return [filterSportId].indexOf(league.sport_id) > -1;
			});
		}

		if (filterString) {
			availableLeaguesFiltered = availableLeaguesFiltered.filter((obj) =>
				JSON.stringify(obj).toLowerCase().includes(filterString.toLowerCase())
			);
		}

		return availableLeaguesFiltered.sort(sorter);
	}

	const sportcastLeague = new SportcastLeague();
	sportcastLeague
		.makePersistRequest({
			url: `${sportcastLeague.resourceName()}/`,
			method: 'GET'
		})
		.then((response) => {
			availableLeagues = response?.data?.all() ?? [];
		});

	function deleteSportcastCompetitionMapping() {
		sending = true;
		competition
			.deleteSportcastCompetition()
			.then(() => {
				notify({
					type: 'success',
					title: 'Competition successfully updated',
					message: `${competition.name} successfully updated!`,
					timeout: 3
				});
				dispatch('updated');
				open = false;
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem updating competition',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}
	function updateSportcastCompetition() {
		sending = true;
		if (!selectedLeague) {
			errors = { selectedLeague: ['You must select a league to map this competition to'] };
			return;
		}
		const payload = {};
		for (const league of availableLeagues) {
			if (league.league_id == selectedLeague) {
				payload.sportcast_sport_id = league.sport_id;
				payload.sportcast_competition_id = league.competition_id;
				// Manual override sport 1 to 7 because sportcast are dumb...
				if (payload.sportcast_sport_id == 1) {
					payload.sportcast_sport_id = 7;
				}
				break;
			}
		}
		competition
			.updateSportcastCompetition(payload)
			.then(() => {
				notify({
					type: 'success',
					title: 'Competition successfully updated',
					message: `${competition.name} successfully updated!`,
					timeout: 3
				});
				dispatch('updated');
				open = false;
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem updating competition',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}
</script>

{#if open}
	<Modal on:close={() => (open = false)}>
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			Remap sportcast for competition - {competition.name}
		</h2>
		<div class="flex flex-col w-full px-2 pt-2 text-left">
			<h2 class="text-xl font-semibold py-2">Filter leagues</h2>
			<hr />
			<SelectInput label="Sportcast sport" className="py-2 w-4/5 text-left" name="sportId" bind:value={filterSportId}>
				<option value={null}>Select Sport...</option>
				{#each availableSports as sportcastSport (sportcastSport.id)}
					<option value={sportcastSport.id}>{sportcastSport.name}</option>
				{/each}
			</SelectInput>
			<TextInput
				label="Filter text:"
				name="filterText"
				className="py-2 w-4/5 text-left"
				placeholder="Enter text to filter"
				bind:value={filterString}
			/>
			<h2 class="text-xl font-semibold py-2">Select league</h2>
			<hr />
			<SelectInput
				label={'Sportcast League'}
				className="py-4 w-full text-left"
				name="selectedLeague"
				bind:value={selectedLeague}
				errors={errors && errors.selectedLeague}
			>
				<option value={null}>Select League...</option>
				{#each filteredAvailableLeagues as sportcastLeague (sportcastLeague.league_id)}
					{#if parseInt(sportcastLeague.league_id) == selectedLeague}
						<option value={parseInt(sportcastLeague.league_id)} selected
							>{sportcastLeague.name} - {sportcastLeague.sport_name} [CompID = {sportcastLeague.competition_id}]
							(LeagueID = {sportcastLeague.league_id})</option
						>
					{:else}
						<option value={parseInt(sportcastLeague.league_id)}
							>{sportcastLeague.name} - {sportcastLeague.sport_name} [CompID = {sportcastLeague.competition_id}]
							(LeagueID = {sportcastLeague.league_id})</option
						>
					{/if}
				{/each}
			</SelectInput>
			<LoadingButton
				on:click={() => updateSportcastCompetition()}
				loading={sending}
				disabled={$ACLStore && !ACLStore.granted('competitions.update')}
				className="mb-6 btn btn-primary"
			>
				{sending ? 'Updating' : 'Update'}
			</LoadingButton>
			{#if competition.sportcast_competition_id != null}
				<LoadingButton
					on:click={() => deleteSportcastCompetitionMapping()}
					loading={sending}
					disabled={$ACLStore && !ACLStore.granted('competitions.update')}
					className="mb-6 btn bg-danger text-white font-semibold hover:text-black"
				>
					{sending ? 'Removing' : 'Remove existing mapping'}
				</LoadingButton>
			{/if}
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
